import React, { useMemo, useEffect } from 'react'
import styles from './Notifications.module.scss'
import BackButton from '../../components/buttons/BackButton/BackButton'
import { BellIcon, CheckCircleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/solid'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { useNotifications, useMarkAllAsRead } from '../../hooks/notifications'

export const Notifications = () => {
	const navigate = useNavigate()

	// 알림 데이터 조회
	const { data: notificationsData, isLoading, error } = useNotifications()

	// 읽음 처리 mutation
	const markAllAsReadMutation = useMarkAllAsRead()

	// 디버깅용 로그
	console.log('notificationsData:', notificationsData)
	console.log('isLoading:', isLoading)
	console.log('error:', error)

	// 페이지 진입 시 모든 알림 읽음 처리
	useEffect(() => {
		if (!notificationsData || isLoading || error) return

		const data = notificationsData.notifications || notificationsData
		if (!Array.isArray(data)) return

		// 안읽은 알림이 있는 경우에만 읽음 처리
		const hasUnreadNotifications = data.some(
			(notification) =>
				!(notification.isRead !== undefined ? notification.isRead : notification.is_read),
		)
		if (hasUnreadNotifications) {
			console.log('markAllAsReadMutation.mutate()')
			markAllAsReadMutation.mutate()
		}
	}, [notificationsData, isLoading, error])

	// 알림 데이터 처리
	const notifications = useMemo(() => {
		if (!notificationsData) return []

		// 서버 응답이 객체 형태일 경우 data 필드에서 배열 추출
		const data = notificationsData.notifications || []

		// 배열이 아닌 경우 빈 배열 반환
		if (!Array.isArray(data)) {
			console.warn('notifications is not an array:', data)
			return []
		}

		// 서버 snake_case 필드를 화면용 camelCase로 정규화
		return data.map((notification) => ({
			...notification,
			createdAt: notification.createdAt || notification.created_at,
			isRead: notification.isRead !== undefined ? notification.isRead : notification.is_read,
			relatedId: notification.relatedId || notification.related_id,
		}))
	}, [notificationsData])

	// 시간 포맷팅
	const formatTime = (timeString) => {
		if (!timeString) return ''
		const time = dayjs(timeString)
		if (!time.isValid()) return ''
		const now = dayjs()
		const diff = now.diff(time, 'hour')

		if (diff < 1) {
			return '방금 전'
		} else if (diff < 24) {
			return `${diff}시간 전`
		} else if (diff < 168) {
			// 7일
			return `${Math.floor(diff / 24)}일 전`
		} else {
			return time.format('MM/DD')
		}
	}

	// 상태 아이콘 렌더링
	const renderStatusIcon = (status) => {
		switch (status) {
			case 'pending':
				return <ClockIcon style={{ width: '16px', height: '16px', color: '#f59e0b' }} />
			case 'accepted':
				return (
					<CheckCircleIcon style={{ width: '16px', height: '16px', color: '#10b981' }} />
				)
			case 'rejected':
				return <XCircleIcon style={{ width: '16px', height: '16px', color: '#ef4444' }} />
			default:
				return <BellIcon style={{ width: '16px', height: '16px', color: '#6b7280' }} />
		}
	}

	// 알림 클릭 핸들러
	const handleNotificationClick = (notification) => {
		if (notification.type === 'offer_management') {
			if (notification.relatedId) {
				navigate(`/offers/${notification.relatedId}`)
				return
			}
			navigate('/offers')
		} else if (notification.type === 'pre_contract_management') {
			navigate('/precontracts')
		} else if (notification.type === 'contract_management') {
			navigate('/contracts')
		}
	}

	// 알림 아이템 렌더링
	const renderNotificationItem = (notification) => {
		const isClickable =
			notification.type === 'offer_management' ||
			notification.type === 'pre_contract_management' ||
			notification.type === 'contract_management'

		return (
			<div
				key={notification.id}
				className={`${styles.notificationItem} ${!notification.isRead ? styles.unread : ''} ${isClickable ? styles.clickable : ''}`}
				onClick={() => isClickable && handleNotificationClick(notification)}>
				{/* <div className={styles.notificationIcon}>
					{renderStatusIcon(notification.status)}
				</div> */}
				<div className={styles.notificationContent}>
					<div className={styles.notificationHeader}>
						<h3 className={styles.notificationTitle}>{notification.title}</h3>
					</div>
					<p className={styles.notificationMessage}>{notification.content}</p>
					<span className={styles.notificationTime}>
						{formatTime(notification.createdAt)}
					</span>
				</div>
				{!notification.isRead && <div className={styles.unreadDot} />}
			</div>
		)
	}

	// 로딩 상태
	if (isLoading) {
		return (
			<div className={styles.notificationsWrapper}>
				<div className={styles.notificationsContent}>
					<div className={styles.notificationsHeader}>
						<h2 className={styles.notificationsTitle}>알림</h2>
						<span className={styles.explain}>
							계약, 시스템, 마케팅 알림을 확인하세요
						</span>
					</div>
					<div className={styles.loadingContainer}>
						<div className={styles.loadingSpinner}></div>
						<div className={styles.loadingText}>알림을 불러오는 중...</div>
					</div>
				</div>
			</div>
		)
	}

	// 에러 상태
	if (error) {
		return (
			<div className={styles.notificationsWrapper}>
				<div className={styles.notificationsContent}>
					<div className={styles.notificationsHeader}>
						<h2 className={styles.notificationsTitle}>알림</h2>
						<span className={styles.explain}>
							계약, 시스템, 마케팅 알림을 확인하세요
						</span>
					</div>
					<div className={styles.errorContainer}>
						<div className={styles.errorText}>
							알림을 불러오는 중 오류가 발생했습니다.
						</div>
						<button
							className={styles.retryButton}
							onClick={() => window.location.reload()}>
							다시 시도
						</button>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className={styles.notificationsWrapper}>
			<div className={styles.notificationsContent}>
				<div className={styles.notificationsHeader}>
					<h2 className={styles.notificationsTitle}>알림</h2>
					<span className={styles.explain}>모든 알림을 확인하세요</span>
				</div>

				{/* 알림 목록 */}
				<div className={styles.notificationsList}>
					{notifications.length > 0 ? (
						notifications.map(renderNotificationItem)
					) : (
						<div className={styles.emptyState}>
							<div className={styles.emptyTitle}>알림이 없습니다</div>
							<div className={styles.emptyDescription}>
								새로운 알림이 도착하면 여기에 표시됩니다.
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}
